import { Injectable } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { NavigationStart, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { LoggerService } from '@services/logger.service';
import { CompaniesService } from '@services/companies.service';
import { TokenService } from '@services/token.service';

@Injectable()
export class CheckForUpdateService {
  public checkVersion$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    public updates: SwUpdate,
    private router: Router,
    private loggerService: LoggerService,
    private companiesService: CompaniesService,
    private tokenService: TokenService,
  ) {
    router.events
      .pipe(
        filter((e: any) => e instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((e: any) => {
        this.checkVersion$.next(true);
      });
  }

  public checkForUpdates(): void {
    this.updates.versionUpdates
      .pipe(
        filter((event: VersionEvent) => event.type === 'VERSION_READY'),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.tokenService.isAuthenticated && this.tokenService.getRefreshToken()) {
          this.loggerService.logEvent(`NEW_VERSION_READY_CONFIRM_SHOWN`, {
            route: location.pathname,
            version: localStorage.getItem('version-raw'),
            externalCompanyId: this.companiesService.companyExternalIds?.externalCompanyId,
            externalUserId: this.companiesService.userExternalId,
            acceptedTermsDate: this.companiesService.userLegalTermsAcceptedDate,
            acceptedPolicyDate: this.companiesService.userPrivacyPolicyAcceptedDate,
            timestamp: new Date(),
            isShouldSend: true,
          });
          this.promptUser();
        } else {
          this.loggerService.logEvent(`NEW_VERSION_READY_CONFIRM_NOT_SHOWN_AS_USER_UNLOGGED`, {
            route: location.pathname,
            version: localStorage.getItem('version-raw'),
            timestamp: new Date(),
            isShouldSend: true,
          });
        }
      });
  }

  public clearCache(): void {
    caches.keys().then((cacheNames: string[]) => {
      return Promise.all(
        cacheNames.filter((cacheName: string) => cacheName.startsWith('ngsw')).map((cacheName: string) => caches.delete(cacheName)),
      );
    });
  }

  private promptUser(): void {
    if (confirm('New version available. Load New Version?')) {
      this.loggerService.logEvent(`NEW_VERSION_READY_CONFIRM_ACCEPTED`, {
        route: location.pathname,
        version: localStorage.getItem('version-raw'),
        externalCompanyId: this.companiesService.companyExternalIds?.externalCompanyId,
        externalUserId: this.companiesService.userExternalId,
        acceptedTermsDate: this.companiesService.userLegalTermsAcceptedDate,
        acceptedPolicyDate: this.companiesService.userPrivacyPolicyAcceptedDate,
        timestamp: new Date(),
        isShouldSend: true,
      });

      this.activateUpdate();
    }
  }

  private activateUpdate(): void {
    this.updates.activateUpdate().then(() => {
      this.loggerService.logEvent(`NEW_VERSION_ACTIVATED`, {
        route: location.pathname,
        version: localStorage.getItem('version-raw'),
        externalCompanyId: this.companiesService.companyExternalIds?.externalCompanyId,
        externalUserId: this.companiesService.userExternalId,
        acceptedTermsDate: this.companiesService.userLegalTermsAcceptedDate,
        acceptedPolicyDate: this.companiesService.userPrivacyPolicyAcceptedDate,
        timestamp: new Date(),
        isShouldSend: true,
      });

      document.location.reload();
    });
  }
}
